/**
 * Host-frame notifications.
 *
 * When a Vibecade-integrated game is embedded inside a launcher
 * iframe, the launcher's chrome (Play.tsx) listens for postMessage
 * events with specific shapes and renders toast UI in response. The
 * SDK auto-emits these on the relevant write events (score submit,
 * achievement unlock) so game devs don't have to duplicate the call.
 *
 * Outside an iframe (`window.parent === window`) this is a no-op —
 * keeps standalone games and direct-URL test harnesses unaffected.
 *
 * `targetOrigin: '*'` is intentional: the SDK runs in the GAME's
 * origin and doesn't know the launcher's origin ahead of time. The
 * receiving launcher origin-checks every inbound message against
 * the configured `games.game_url` origin before acting on it, so
 * the launcher side enforces the origin contract — not us.
 *
 * Every auto-emitted message carries `source: 'vibecade-sdk'` so a
 * launcher that receives both an SDK auto-post and a manual game
 * post for the same event can dedupe by source if it wants to.
 * Today the launcher just toasts both, which is why we also expose
 * the `autoHostMessages: false` config flag for legacy integrations
 * (pre-0.1.1) that emit their own postMessage and would double up.
 */

export type HostMessage =
  | { type: 'achievement_unlocked'; achievementId: string; displayName: string; source: 'vibecade-sdk' }
  // `boardKey` is present when the score was filed under one board of a
  // multi-board game, and its absence means the game has one leaderboard.
  // It matters because `rank` is then BOARD-LOCAL: rank 1 on today's daily is
  // not rank 1 for the game. A host that renders the rank without reading this
  // will announce a podium the game's own aggregate leaderboard contradicts.
  | { type: 'score_submitted'; rank: number; boardKey?: string; source: 'vibecade-sdk' };

/**
 * Post a structured message to the embedding launcher iframe parent.
 * `enabled` short-circuits the call when the consumer has opted out
 * of auto host notifications (config.autoHostMessages = false).
 */
export function postToHost(message: HostMessage, enabled: boolean): void {
  if (!enabled) return;
  if (typeof window === 'undefined') return;
  if (window.parent === window) return;
  try {
    window.parent.postMessage(message, '*');
  } catch {
    // Cross-origin parents that refuse postMessage. Treat as no-op;
    // the SDK's primary side-effect (DB write) already succeeded.
  }
}
